import { baseUrl } from "./Configuration";

export interface User {
  id: string;
  userName: string;
  isPrimaryUser: boolean;
  createdAt: string;
}

export async function fetchUsers(): Promise<User[]> {
  const token = localStorage.getItem('token');
  const url = `${baseUrl}/api/Users`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    if (response.status === 401) {
      localStorage.clear();
      window.location.href = "/login";
      return [];
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: `HTTP ${response.status}: ${response.statusText}`,
      }));
      throw new Error(errorData.message || "Failed to fetch users");
    }
    return await response.json();
  } catch (error: any) {
    console.error("Error fetching users:", error);
    if (error.message === "Failed to fetch") {
      throw new Error(
        "Unable to connect to server. Please check your connection and try again."
      );
    }
    throw error;
  }
}

export async function createUser(userData: {
  userName: string;
  password: string;
  isPrimaryUser: boolean;
}): Promise<void> {
  const token = localStorage.getItem('token');
  const url = `${baseUrl}/api/Users`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(userData),
    });
    if (response.status === 401) {
      localStorage.clear();
      window.location.href = "/login";
      return;
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: `HTTP ${response.status}: ${response.statusText}`,
      }));
      throw new Error(errorData.message || "Failed to create user");
    }
  } catch (error: any) {
    console.error("Error creating user:", error);
    if (error.message === "Failed to fetch") {
      throw new Error(
        "Unable to connect to server. Please check your connection and try again."
      );
    }
    throw error;
  }
}

export async function updateUser(
  userId: string,
  userData: { userName: string; password?: string; isPrimaryUser: boolean }
): Promise<void> {
  const token = localStorage.getItem('token');
  const url = `${baseUrl}/api/Users/${userId}`;
  const updateData = { ...userData };
  if (!updateData.password) {
    delete updateData.password;
  }

  try {
    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(updateData),
    });
    if (response.status === 401) {
      localStorage.clear();
      window.location.href = "/login";
      return;
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: `HTTP ${response.status}: ${response.statusText}`,
      }));
      throw new Error(errorData.message || "Failed to update user");
    }
  } catch (error: any) {
    console.error("Error updating user:", error);
    if (error.message === "Failed to fetch") {
      throw new Error(
        "Unable to connect to server. Please check your connection and try again."
      );
    }
    throw error;
  }
}

export async function deleteUser(userId: string): Promise<void> {
  const token = localStorage.getItem('token');
  const url = `${baseUrl}/api/Users/${userId}`;

  try {
    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (response.status === 401) {
      localStorage.clear();
      window.location.href = "/login";
      return;
    }
    if (!response.ok) {
      throw new Error("Failed to delete user");
    }
  } catch (error: any) {
    console.error("Error deleting user:", error);
    if (error.message === "Failed to fetch") {
      throw new Error(
        "Unable to connect to server. Please check your connection and try again."
      );
    }
    throw error;
  }
}